import { useState, useEffect } from 'react'

export const localStorageUtils = {
  getItem: (key, defaultValue) => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error)
      return defaultValue
    }
  },

  setItem: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error)
    }
  },

  removeItem: (key) => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error)
    }
  },

  clear: () => {
    try {
      localStorage.clear()
    } catch (error) {
      console.error('Error clearing localStorage:', error)
    }
  }
}

export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      return localStorageUtils.getItem(key, initialValue)
    } catch (error) {
      console.error(error)
      return initialValue
    }
  })

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      localStorageUtils.setItem(key, valueToStore)
    } catch (error) {
      console.error(error)
    }
  }

  return [storedValue, setValue]
}

export const useFileSystem = () => {
  const [files, setFiles] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadFiles()
  }, [])

  const loadFiles = () => {
    try {
      const savedFiles = localStorageUtils.getItem('codeFiles', {})
      setFiles(savedFiles)
    } catch (error) {
      console.error('Error loading files:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveFile = (file) => {
    const updatedFiles = { ...files, [file.id]: file }
    localStorageUtils.setItem('codeFiles', updatedFiles)
    setFiles(updatedFiles)
    return file
  }

  const deleteFile = (fileId) => {
    const updatedFiles = { ...files }
    delete updatedFiles[fileId]
    localStorageUtils.setItem('codeFiles', updatedFiles)
    setFiles(updatedFiles)
  }

  const createFile = (name, content = '', path = '/', language = 'javascript') => {
    const file = {
      id: Date.now().toString(),
      name,
      type: 'file',
      language,
      content,
      path,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    return saveFile(file)
  }

  const createFolder = (name, path = '/') => {
    const folder = {
      id: `folder-${Date.now()}`,
      name,
      type: 'folder',
      path,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    return saveFile(folder)
  }

  const importFiles = (importedFiles) => {
    const updatedFiles = { ...files, ...importedFiles }
    localStorageUtils.setItem('codeFiles', updatedFiles)
    setFiles(updatedFiles)
  }

  const exportFiles = () => {
    return files
  }

  const getFileStats = () => {
    const fileArray = Object.values(files)
    return {
      totalFiles: fileArray.length,
      totalFolders: fileArray.filter(f => f.type === 'folder').length,
      totalSize: fileArray.reduce((sum, file) => sum + (file.content?.length || 0), 0),
      languages: [...new Set(fileArray.map(f => f.language))].filter(Boolean),
      lastModified: fileArray.sort((a, b) => 
        new Date(b.updatedAt) - new Date(a.updatedAt)
      )[0]?.updatedAt
    }
  }

  return {
    files,
    loading,
    saveFile,
    deleteFile,
    createFile,
    createFolder,
    importFiles,
    exportFiles,
    getFileStats,
    loadFiles
  }
}